<template>
  <div class="availability-form-base">
    <div class="space-y-2">
      <div class="grid grid-cols-12 gap-x-2">
        <div class="col-span-6 grid grid-cols-12 gap-x-2">
          <label class="col-span-8">
            <span class="block text-sm font-medium select-none"> Vanaf </span>
            <InputDatepicker
              v-model="startDate"
              placeholder="DD-MM-YY"
              :disabled="disabled"
              @update:model-value="save"
            />
          </label>
          <label class="col-span-4">
            <span class="invisible block text-sm font-medium">Starttijd</span>
            <InputTimepicker
              v-model="startTime"
              :disabled="disabled || !startDate"
              @focusout="onFocusOutTimeStart"
              ref="startTimePicker"
            />
          </label>
        </div>
        <div class="col-span-6 grid grid-cols-12 gap-x-2">
          <label class="col-span-8">
            <span class="block text-sm font-medium select-none">Tot en met</span>
            <InputDatepicker
              v-model="endDate"
              placeholder="DD-MM-YY"
              :disabled="disabled || !startDate"
              @update:model-value="save"
            />
          </label>
          <label class="col-span-4">
            <span class="invisible block text-sm font-medium select-none">Eindtijd</span>
            <InputTimepicker
              ref="endTimePicker"
              v-model="endTime"
              :disabled="disabled || !endDate"
              @focusout="onFocusOutTimeEnd"
            />
          </label>
        </div>
        <div v-if="v$.$silentErrors.length > 0" class="col-span-12">
          <span class="text-error text-sm select-none">
            {{ v$.$silentErrors[0].$message }}
          </span>
        </div>
      </div>

      <label class="block">
        <span class="font-mediu block text-sm select-none">Opmerking</span>
        <InputTextarea
          v-model="availabilityBlock.description!"
          placeholder="Aanvullende informatie over inzetbaarheid"
          :disabled="disabled"
          autosize
          class="w-full"
          @focusout="save"
        />
      </label>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, inject, ref } from "vue"
import type { Moment } from "moment"
import useVuelidate from "@vuelidate/core"
import InputDatepicker from "@/components/inputs/input_datepicker/InputDatepicker.vue"
import InputTextarea from "@/components/inputs/InputTextarea.vue"
import InputTimepicker from "@/components/inputs/InputTimepicker.vue"
import type { AvailabilityBucket } from "@/models/availability-bucket.model.ts"
import type { AvailabilityBlockController } from "@/controllers/model-controllers/availability-block-controller.ts"
import type { AvailabilityBlock } from "@/models/availability-block.model.ts"
import {
  getDatePortion,
  getTimePortion,
  isMidnight,
  setDatePortion,
  setTimePortion
} from "@/helper/time-helper.ts"
import { chronologicalStartAndEndTimeValidator } from "@/helper/validation/time-validation.ts"

const props = withDefaults(
  defineProps<{
    availabilityBucket: AvailabilityBucket
    disabled?: boolean
  }>(),
  {
    disabled: false
  }
)

const availabilityBlockController = inject<AvailabilityBlockController>(
  "availabilityBlockController"
)!

const availabilityBlock = ref<AvailabilityBlock>(
  props.availabilityBucket.getBaseAvailabilityBlock()
)

// Split timeStart into separate date and time computed refs for the input controls.
const startDate = computed<Moment | null>({
  get: () => getDatePortion(availabilityBlock.value.timeStart),
  set: (value) =>
    (availabilityBlock.value.timeStart = setDatePortion(availabilityBlock.value.timeStart, value))
})

const startTime = computed<Moment | null>({
  get: () => getTimePortion(availabilityBlock.value.timeStart),
  set: (value) =>
    (availabilityBlock.value.timeStart = setTimePortion(availabilityBlock.value.timeStart, value))
})

// Split timeEnd into separate date and time computed refs for the input controls.
const endDate = computed<Moment | null>({
  get: () => getDatePortion(availabilityBlock.value.timeEnd),
  set: (value) =>
    (availabilityBlock.value.timeEnd = setDatePortion(availabilityBlock.value.timeEnd, value))
})

const endTime = computed<Moment | null>({
  get: () => getTimePortion(availabilityBlock.value.timeEnd),
  set: (value) =>
    (availabilityBlock.value.timeEnd = setTimePortion(availabilityBlock.value.timeEnd, value))
})

// Clear the time picker if the time is midnight
const startTimePicker = ref<InstanceType<typeof InputTimepicker>>()
function onFocusOutTimeStart() {
  if (isMidnight(availabilityBlock.value.timeStart)) {
    startTimePicker.value?.clear()
  }
  save()
}

// Clear the time picker if the time is midnight
const endTimePicker = ref<InstanceType<typeof InputTimepicker>>()
function onFocusOutTimeEnd() {
  if (isMidnight(availabilityBlock.value.timeEnd)) {
    endTimePicker.value?.clear()
  }
  save()
}

async function save() {
  const isValid = await v$.value.$validate()
  if (!isValid) {
    return
  }

  await availabilityBlockController.update(availabilityBlock.value)
}

// VALIDATION RULES
const validationRules = computed(() => ({
  range: {
    timeRange: chronologicalStartAndEndTimeValidator({
      allowEqual: true,
      treatMidnightAsEndOfDay: true
    })
  }
}))

const validationState = computed(() => ({
  range: availabilityBlock.value
}))

const v$ = useVuelidate(validationRules, validationState)
</script>

<style scoped></style>
